import { useState, useMemo } from "react";
import ScreenContainer from "../../components/ScreenContainer";
import { CommonActions } from "@react-navigation/native";
import ScrollableContainer from "../../components/ScrollableContainer";
import { Text, View, TouchableOpacity, Platform } from "react-native";
import FilterIcon from "../../svgs/FilterIcon";
import { Badge } from "react-native-paper";
import { ScreenProps } from "./types";
import PlayCircle from "../../svgs/PlayCircle";
import { useQuery } from "@apollo/client";
import { GET_PDR_VIDEOS } from "./queries";
import { ActivityIndicator } from "react-native";
import { videoData } from "./types";
import FilterModal from "./FilterModal";
import { getCreditCategory, renderVideoStatusChip } from "./utils";
import { ScrollView } from "../../components";
import { useEffect } from "react";
import { Linking } from "react-native";

export const navigationTitle = "PDR Videos";

export default function PDRVideosListScreen({ navigation }: ScreenProps<"Videos">) {
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [creditTypeFilters, setCreditTypeFilters] = useState<{ [key: string]: boolean }>({});
  const [seriesFilters, setSeriesFilters] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    const stackLength = navigation.getState().routes.length;

    if (stackLength === 1) {
      navigation.dispatch(
        CommonActions.reset({
          index: 1,
          routes: [{ name: "Main" }, { name: "Videos" }],
        })
      );
    }

    const unsubscribe = navigation.addListener("beforeRemove", (event) => {
      const currentStackLength = navigation.getState().routes.length;
      if (currentStackLength === 1) {
        event.preventDefault();
        navigation.navigate("Main");
      }
    });

    return unsubscribe;
  }, [navigation]);

  const activeFilters = useMemo(() => {
    return {
      creditCategory: Object.keys(creditTypeFilters).filter((key) => creditTypeFilters[key]),
      series: Object.keys(seriesFilters).filter((key) => seriesFilters[key]),
    };
  }, [creditTypeFilters, seriesFilters]);

  const { data, loading, error, refetch } = useQuery(GET_PDR_VIDEOS, {
    variables: activeFilters,
  });

  const sortedVideos = useMemo(() => {
    if (!data || !data.videos) return [];
    return [...data.videos].sort((a, b) => {
      const order: { [key: string]: number } = {
        "In Progress": 0,
        Expiring: 1,
        Completed: 3,
        Unavailable: 4,
      };
      const getOrder = (status: string | null) => {
        if (status && status.startsWith("Expiring")) return order["Expiring"];
        return status ? order[status] ?? 5 : 2;
      };
      return getOrder(a.memberVideoStatus) - getOrder(b.memberVideoStatus);
    });
  }, [data]);

  function navigateToPDRVideoDetails(video: videoData) {
    navigation.navigate("VideosDetails", {
      videoId: video.id,
    });
    if (Platform.OS === "web") {
      // @ts-ignore
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }

  function handleFilterPress() {
    setShowFiltersModal(true);
  }

  function handleDismissModal() {
    setShowFiltersModal(false);
    refetch(activeFilters);
  }

  function handleChangeCreditType(value: string) {
    setCreditTypeFilters((prev) => ({ ...prev, [value]: !prev[value] }));
  }

  function handleChangeSeries(value: string) {
    setSeriesFilters((prev) => ({ ...prev, [value]: !prev[value] }));
  }

  function handleResetFilters() {
    setCreditTypeFilters({});
    setSeriesFilters({});
  }

  function FiltersButton({ onPress, count, disabled }: { onPress(): void; count: number; disabled: boolean }) {
    return (
      <TouchableOpacity
        {...{ disabled, onPress }}
        testID="pdr-videos-filter-button"
        className="self-start my-4 mx-6 border border-ink-lighter rounded-xl flex-row justify-between px-3 py-2 items-center"
      >
        <FilterIcon />
        <Text className={`text-small-normal pl-2 ${count > 0 ? "text-primary pr-2" : "text-ink-dark pr"}`}>Filters</Text>
        {count > 0 && <Badge className="font-700 bg-secondary-light">{count}</Badge>}
      </TouchableOpacity>
    );
  }

  if (loading) {
    return (
      <ScreenContainer navigationTitle={navigationTitle} noBackButton={false} testID="videos-list-screen">
        <ScrollableContainer>
          <ActivityIndicator size="large" className="flex-1 items-center justify-center bg-white mt-10" />
        </ScrollableContainer>
      </ScreenContainer>
    );
  }

  if (!!error) return <Text className="text-red-dark py-2">{error?.message}</Text>;

  if (!data) return <Text className="text-red-dark py-2">GET_PDR_VIDEOS returned no data</Text>;

  const activeFilterCount =
    Object.values(creditTypeFilters).filter(Boolean).length + Object.values(seriesFilters).filter(Boolean).length;

  return (
    <ScreenContainer navigationTitle={navigationTitle} testID="videos-list-screen">
      <ScrollView>
        <View className="flex-row justify-between mb-[-2]">
          <FiltersButton onPress={handleFilterPress} count={activeFilterCount} disabled={false} />
        </View>
        <View className="pt-7">
          {(activeFilters.creditCategory.length !== 0 && !activeFilters.creditCategory.includes("EDUCATION")) ||
          activeFilters.series.length >= 1 ? null : (
            <TouchableOpacity
              activeOpacity={0.7}
              onPress={() =>
                Linking.openURL(
                  "https://vt.lightspeedvt.com/share/redirect.cfm?signInURL=https://webservices.lightspeedvt.net/SAML/lsvt/login.aspx?h=auth0.pga.com&dest=https://vt.lightspeedvt.com/trainingCenter/"
                )
              }
              className="flex-row pb-7 border-b border-sky-light bg-sky-gray-light items-center mb-7"
            >
              <View className="ml-6 mr-1">
                <PlayCircle />
              </View>
              <View className="flex-1 ml-2 pr-6">
                {renderVideoStatusChip("Go to Course")}
                <Text className="text-regular text-ink-darkest font-700">PGA Coach Learning Center (7 Courses)</Text>
                <View className="flex-row mt-2">
                  <Text className="text-ink-base text-small font-normal leading-none">45m+</Text>
                  <Text className="text-ink-base text-small font-normal leading-none ml-2">Education</Text>
                  <Text className="text-ink-base text-small font-normal leading-none ml-2">15 PDR Credits</Text>
                </View>
              </View>
            </TouchableOpacity>
          )}
        </View>
        <View className="mb-8" testID="PDRVideosScreen">
          {sortedVideos.map((video: videoData, key: number) => (
            <TouchableOpacity
              activeOpacity={0.7}
              onPress={() => navigateToPDRVideoDetails(video)}
              key={key}
              className="flex-row pb-6 border-b border-sky-light bg-sky-gray-light items-center mb-6"
            >
              <View className="ml-6 mr-1">
                <PlayCircle />
              </View>
              <View className="flex-1 ml-2 pr-6">
                {video?.memberVideoStatus && renderVideoStatusChip(video.memberVideoStatus)}
                <Text className="text-regular text-ink-darkest font-700">{video.title}</Text>
                <View className="flex-row mt-2">
                  <Text className="text-ink-base text-small font-normal leading-none">{video.duration}m</Text>
                  <Text className="text-ink-base text-small font-normal leading-none ml-2">
                    {getCreditCategory(video.creditCategory)}
                  </Text>
                  <Text className="text-ink-base text-small font-normal leading-none ml-2">1 PDR Credit</Text>
                </View>
              </View>
            </TouchableOpacity>
          ))}
        </View>
        <FilterModal
          visible={showFiltersModal}
          onDismiss={handleDismissModal}
          creditTypeFilters={creditTypeFilters}
          seriesFilters={seriesFilters}
          onChangeCreditType={handleChangeCreditType}
          onChangeSeries={handleChangeSeries}
          onReset={handleResetFilters}
          videoSeriesOptions={data.videoSeries}
        />
      </ScrollView>
    </ScreenContainer>
  );
}
